import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { TrackByPath, TrackMenuExitButton } from './SiteHeaderTracking';
import { KlevuRecord, KMCBannerRootObject, KMCMapsRootObject, KMCRootObject } from '@klevu/core';

type AppState = {
  isMenuVisible: boolean;
  selectedMenu: any;
  showSearch: boolean;
  products: KlevuRecord[];
  menuLevel: number;
  selectedLinkSection: any;
  selectedProductSection: any;
  selectedCategory: any;
  selectedLink: any;
  searchTerm: string;
  sugs: string[];
  categories: KlevuRecord[];
  kmcSettings: {
    root?: KMCRootObject;
    banner?: KMCBannerRootObject;
    maps?: KMCMapsRootObject;
  };
  setIsMenuVisible: (isVisible: boolean) => void;
  setSelectedMenu: (menu: any) => void;
  setShowSearch: (show: boolean) => void;
  setProducts: (products: KlevuRecord[]) => void;
  setMenuLevel: (level: number) => void;
  setSelectedLinkSection: (section: any) => void;
  setSelectedProductSection: (section: any) => void;
  setSelectedCategory: (category: any) => void;
  setSelectedLink: (link: any) => void;
  setSearchTerm: (term: string) => void;
  setSuggestions: (suggestions: string[]) => void;
  setCategories: (categories: KlevuRecord[]) => void;
  setKmcSettings: (settings: { root?: KMCRootObject; banner?: KMCBannerRootObject; maps?: KMCMapsRootObject }) => void;
};

const AppStateContext = createContext({} as AppState);

const AppStateProvider = ({ children }) => {
  const hasMounted = useRef(false);
  const [isMenuVisible, setIsMenuVisible] = useState<boolean>(false);
  const [selectedMenu, setSelectedMenu] = useState<any>();
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [products, setProducts] = useState<KlevuRecord[]>([]);
  const [menuLevel, setMenuLevel] = useState<number>(1);

  const [selectedLinkSection, setSelectedLinkSection] = useState<any>();
  const [selectedProductSection, setSelectedProductSection] = useState<any>();
  const [selectedCategory, setSelectedCategory] = useState<any>();
  const [selectedLink, setSelectedLink] = useState<any>();

  const [searchTerm, setSearchTerm] = useState<string>('');
  const [sugs, setSuggestions] = useState<string[]>([]);
  const [categories, setCategories] = useState<KlevuRecord[]>([]);
  const [kmcSettings, setKmcSettings] = useState<{
    root?: KMCRootObject;
    banner?: KMCBannerRootObject;
    maps?: KMCMapsRootObject;
  }>();

  useEffect(() => {
    if (showSearch && isMenuVisible) setIsMenuVisible(false);
  }, [showSearch]);

  useEffect(() => {
    if (!selectedMenu) return;
    let secondLevelTitle: string = '';
    if (selectedLinkSection) secondLevelTitle = selectedLinkSection.EnglishTitle;
    else if (selectedProductSection) secondLevelTitle = selectedProductSection.EnglishTitle;

    TrackByPath(selectedMenu?.EnglishTitle, secondLevelTitle, selectedCategory?.EnglishTitle, selectedLink);
  }, [selectedMenu, selectedLinkSection, selectedProductSection, selectedCategory, selectedLink]);

  useEffect(() => {
    if (!isMenuVisible) {
      setSelectedMenu(null);
      setMenuLevel(1);
    }
    document.body.classList.toggle('overflow-hidden', isMenuVisible);
    document.documentElement.classList.toggle('overflow-hidden', isMenuVisible);

    if (!isMenuVisible && hasMounted.current === true) TrackMenuExitButton();

    hasMounted.current = true;
    if (isMenuVisible && showSearch) setShowSearch(false);
  }, [isMenuVisible]);

  return (
    <AppStateContext.Provider
      value={{
        selectedMenu,
        setSelectedMenu,
        isMenuVisible,
        setIsMenuVisible,
        showSearch,
        setShowSearch,
        products,
        setProducts,
        menuLevel,
        setMenuLevel,
        selectedLinkSection,
        setSelectedLinkSection,
        selectedProductSection,
        setSelectedProductSection,
        selectedCategory,
        setSelectedCategory,
        selectedLink,
        setSelectedLink,
        searchTerm,
        setSearchTerm,
        sugs,
        setSuggestions,
        categories,
        setCategories,
        kmcSettings: kmcSettings ?? {},
        setKmcSettings,
      }}
    >
      {children}
    </AppStateContext.Provider>
  );
};

const useAppState = () => {
  const context = useContext(AppStateContext);
  if (!context) {
    throw new Error('useAppState must be used within an AppStateProvider');
  }
  return context;
};

export { AppStateProvider, useAppState };
